import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { MarketIndices } from '../components/MarketIndices';
import { useAuth } from '../context/AuthContext';
import { 
  Activity, Layers, Shield, Lock, ArrowRight, Zap, 
  KeyRound, Globe, Wallet, CheckCircle, Clock, Gem,
  TrendingUp, Users, BarChart3, Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Opportunities() {
  const { user } = useAuth();
  const hasDTAccess = user?.digital_twin_access === 'active';
  const [demoStep, setDemoStep] = useState(0);
  const [showDemo, setShowDemo] = useState(false);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="tokenisation-hub">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Layers size={20} className="text-gold" />
            <p className="text-gold uppercase tracking-[0.2em] text-xs">Tokenisation Hub</p>
          </div>
          <h1 className="font-serif text-3xl text-white">Tokenise Your Assets</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            Transform your luxury assets into blockchain-secured Digital Twins. Explore the process, understand the benefits, and take the first step.
          </p>
        </div>

        {/* Live Market Indices */}
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-6">
            <Activity size={18} className="text-gold" />
            <h2 className="font-serif text-xl text-white">Live Market Indices</h2>
          </div>
          <MarketIndices compact={true} />
        </div>

        {/* Why Fractional - Generalised Value Prop */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="why-fractional">
          {[
            { icon: Users, title: 'Diversify', desc: 'Own pieces of multiple luxury assets instead of one', color: 'text-[#627EEA]' },
            { icon: Gem, title: 'Access', desc: 'Invest in assets previously beyond reach', color: 'text-gold' },
            { icon: Repeat, title: 'Liquidity', desc: 'Sell your fractions anytime, 24/7 on-chain', color: 'text-emerald-400' },
            { icon: Shield, title: 'No Middleman', desc: 'Direct peer-to-peer ownership, instant settlement', color: 'text-gold' },
          ].map((item, i) => (
            <div key={i} className="luxury-card p-5 text-center">
              <item.icon size={24} className={`${item.color} mx-auto mb-3`} />
              <h3 className="text-white text-sm font-medium mb-1">{item.title}</h3>
              <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </section>


        {/* Why Tokenise YOUR Asset + Why Buy Fractions */}
        <section className="grid md:grid-cols-2 gap-4 mb-10" data-testid="why-tokenise">
          <div className="luxury-card p-5">
            <h3 className="text-gold text-sm font-medium mb-4">Why Tokenise Your Asset?</h3>
            <div className="space-y-3">
              {[
                { text: 'Unlock liquidity without selling the whole thing', sub: 'Sell 10% of your Ferrari, keep driving the other 90%' },
                { text: 'Prove provenance permanently', sub: 'Immutable record increases value at auction' },
                { text: 'Access global buyers', sub: 'Not just local collectors, anyone worldwide' },
                { text: 'Use as collateral for loans', sub: 'Borrow against your asset without selling it' },
                { text: 'Pass to heirs with verified trail', sub: 'Generational wealth transfer, zero disputes' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs text-white">{item.text}</p><p className="text-[10px] text-muted-foreground">{item.sub}</p></div>
                </div>
              ))}
            </div>
          </div>

          <div className="luxury-card p-5">
            <h3 className="text-[#627EEA] text-sm font-medium mb-4">Why Buy Fractions?</h3>
            <div className="space-y-3">
              {[
                { text: 'Own a piece of a £3M asset for £3,000', sub: 'Accessible entry point to ultra-luxury' },
                { text: 'Diversify across asset classes', sub: 'Watches + art + cars + wine in one portfolio' },
                { text: 'Luxury assets appreciate 8-12% annually', sub: 'Historically outperform equities and property' },
                { text: 'No maintenance responsibility', sub: 'No storage, insurance, or upkeep costs for you' },
                { text: 'Buy or sell 24/7 with instant settlement', sub: 'Not locked up like traditional alternatives' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle size={12} className="text-[#627EEA] mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs text-white">{item.text}</p><p className="text-[10px] text-muted-foreground">{item.sub}</p></div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* The Assagart Method - Visual Cards */}
        <section className="mb-10" data-testid="tokenisation-method">
          <div className="flex items-center gap-2 mb-6">
            <Layers size={16} className="text-gold" />
            <h2 className="font-serif text-xl text-white">The Assagart Method</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { num: 'I', name: 'Ingestion', icon: Shield, desc: 'Asset enters the protocol. Authentication, valuation, and risk profiling are completed.', color: 'border-emerald-500/20' },
              { num: 'II', name: 'Anchoring', icon: Lock, desc: 'Digital Twin minted on-chain. Ownership rights and provenance cryptographically sealed.', color: 'border-[#627EEA]/20' },
              { num: 'III', name: 'Activation', icon: Zap, desc: 'Asset becomes ownable. Fractionalize, list on marketplace, or use as collateral.', color: 'border-gold/20' },
            ].map((step, i) => (
              <div key={i} className={`luxury-card p-5 border-l-2 ${step.color}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-gold font-serif text-xl">{step.num}</span>
                  <step.icon size={16} className="text-muted-foreground" />
                  <h3 className="text-white font-medium text-sm">{step.name}</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Combined Reference: Timelines + Fees + What You Get */}
        <section className="luxury-card p-6 mb-10" data-testid="quick-reference">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Clock size={16} className="text-gold mb-3" />
              <h4 className="text-white text-sm font-medium mb-2">Timelines</h4>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Approval</span><span className="text-white">24-48 hours</span></div>
                <div className="flex justify-between"><span>Minting</span><span className="text-white">Under 2 min</span></div>
                <div className="flex justify-between"><span>Listing</span><span className="text-white">Immediate</span></div>
                <div className="flex justify-between"><span>Settlement</span><span className="text-white">Instant</span></div>
              </div>
            </div>
            <div>
              <BarChart3 size={16} className="text-gold mb-3" />
              <h4 className="text-white text-sm font-medium mb-2">Fees</h4>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex justify-between"><span>Tokenization</span><span className="text-white">Included</span></div>
                <div className="flex justify-between"><span>Platform fee</span><span className="text-white">1.5%</span></div>
                <div className="flex justify-between"><span>Gas fees</span><span className="text-white">Covered by Assagart</span></div>
                <div className="flex justify-between"><span>Un-tokenize</span><span className="text-white">Free</span></div>
              </div>
            </div>
          </div>
          <div className="pt-4 border-t border-[#1A1A1A]">
            <Globe size={16} className="text-gold mb-3" />
            <h4 className="text-white text-sm font-medium mb-2">What You Get</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2"><CheckCircle size={10} className="text-emerald-400" /> On-chain ownership proof</div>
              <div className="flex items-center gap-2"><CheckCircle size={10} className="text-emerald-400" /> 24/7 AI valuations</div>
              <div className="flex items-center gap-2"><CheckCircle size={10} className="text-emerald-400" /> Fractional marketplace access</div>
              <div className="flex items-center gap-2"><CheckCircle size={10} className="text-emerald-400" /> Insured custody</div>
            </div>
          </div>
        </section>

        {/* How to Tokenize + Un-Tokenize (Combined) */}
        <section className="luxury-card p-6 mb-10" data-testid="how-it-works">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
                <KeyRound size={14} className="text-gold" /> How to Tokenize
              </h4>
              <div className="space-y-2">
                {['Go to your asset page', 'Click "Activate Digital Twin"', 'Complete the 3-step ceremony', 'Admin verifies and approves', 'Digital Twin minted on-chain'].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="w-5 h-5 rounded-full bg-gold/10 text-gold text-[10px] flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    {step}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-white text-sm font-medium mb-3 flex items-center gap-2">
                <Repeat size={14} className="text-gold" /> Un-Tokenizing
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                You can reclaim your asset at any time. If you hold 100% ownership, submit an un-tokenization request. The Digital Twin is burned, and your asset returns to standard management with full history preserved.
              </p>
              <p className="text-[10px] text-muted-foreground italic">If fractions have been sold, buy them back first. No penalty fees apply.</p>
            </div>
          </div>
        </section>

        {/* Try Demo Purchase */}
        <section className="luxury-card p-6 mb-10" data-testid="demo-trade">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wallet size={16} className="text-[#627EEA]" />
              <h3 className="text-white text-sm font-medium">Try a Demo Purchase</h3>
            </div>
            {!showDemo && (
              <Button onClick={() => { setShowDemo(true); setDemoStep(0); }} variant="outline" className="border-[#627EEA]/30 text-[#627EEA] hover:bg-[#627EEA]/10 text-xs" data-testid="start-demo-btn">
                Start Demo
              </Button>
            )}
          </div>

          {!showDemo ? (
            <p className="text-xs text-muted-foreground">Experience the full buy flow without spending anything. Walk through connecting a wallet, selecting fractions, confirming a purchase, and understanding what happens next.</p>
          ) : (
            <div className="space-y-4">
              {/* Progress */}
              <div className="flex gap-2">
                {['Connect', 'Select', 'Confirm', 'Receipt', 'Your Tokens', 'What Next'].map((label, i) => (
                  <div key={i} className={`flex-1 h-1 rounded-full ${i <= demoStep ? 'bg-[#627EEA]' : 'bg-[#1A1A1A]'}`} />
                ))}
              </div>

              {demoStep === 0 && (
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-center">
                  <Wallet size={24} className="text-[#F6851B] mx-auto mb-2" />
                  <p className="text-white text-sm mb-1">Connect Your Wallet</p>
                  <p className="text-xs text-muted-foreground mb-3">MetaMask pops up asking you to connect your Ethereum address.</p>
                  <Button onClick={() => setDemoStep(1)} className="bg-[#F6851B] hover:bg-[#E07A18] text-white text-xs">Simulate Connection</Button>
                </div>
              )}

              {demoStep === 1 && (
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
                  <p className="text-white text-sm mb-3">Select fractions to buy</p>
                  <div className="p-3 bg-[#0F0F0F] border border-[#262626] rounded mb-3">
                    <p className="text-xs text-muted-foreground">Asset: <span className="text-white">1965 Ferrari 275 GTB</span></p>
                    <p className="text-xs text-muted-foreground">Price: <span className="text-gold">£3,200 per fraction</span></p>
                    <p className="text-xs text-muted-foreground">Buying: <span className="text-white">5 fractions (£16,000)</span></p>
                  </div>
                  <Button onClick={() => setDemoStep(2)} className="bg-[#627EEA] hover:bg-[#5570D0] text-white text-xs w-full">Review Order</Button>
                </div>
              )}

              {demoStep === 2 && (
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg text-center">
                  <Lock size={24} className="text-gold mx-auto mb-2" />
                  <p className="text-white text-sm mb-1">Confirm Transaction</p>
                  <p className="text-xs text-muted-foreground mb-3">MetaMask asks you to sign. Gas fees are covered by Assagart.</p>
                  <Button onClick={() => setDemoStep(3)} className="bg-gold text-black hover:bg-gold/90 text-xs">Sign Transaction</Button>
                </div>
              )}

              {demoStep === 3 && (
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-center">
                  <CheckCircle size={24} className="text-emerald-400 mx-auto mb-2" />
                  <p className="text-white text-sm mb-1">Purchase Complete</p>
                  <p className="text-xs text-muted-foreground mb-2">5 fractions of Ferrari 275 GTB purchased for £16,000</p>
                  <p className="text-[10px] text-muted-foreground font-mono mb-3">TX: 0x7f3a...c921</p>
                  <Button onClick={() => setDemoStep(4)} className="bg-[#627EEA] text-white text-xs">Where Are My Tokens?</Button>
                </div>
              )}

              {demoStep === 4 && (
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
                  <Wallet size={20} className="text-[#627EEA] mb-2" />
                  <p className="text-white text-sm mb-2">Your Tokens Live in Two Places</p>
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded bg-[#F6851B]/10 flex items-center justify-center flex-shrink-0"><Wallet size={10} className="text-[#F6851B]" /></span>
                      <div><p className="text-xs text-white">MetaMask Wallet</p><p className="text-[10px] text-muted-foreground">The actual blockchain record. Open MetaMask, go to "NFTs" tab, and you'll see your fractions listed. You can also import the token contract address manually.</p></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded bg-gold/10 flex items-center justify-center flex-shrink-0"><BarChart3 size={10} className="text-gold" /></span>
                      <div><p className="text-xs text-white">Assagart Dashboard</p><p className="text-[10px] text-muted-foreground">Go to Marketplace &gt; My Holdings to see all your fractions, their current value (updated by Oracle AI), and your total portfolio performance.</p></div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded bg-emerald-400/10 flex items-center justify-center flex-shrink-0"><TrendingUp size={10} className="text-emerald-400" /></span>
                      <div><p className="text-xs text-white">Track Appreciation</p><p className="text-[10px] text-muted-foreground">Each asset page shows a live valuation graph powered by Oracle AI. Watch your token value grow over time as the underlying asset appreciates. The AI analyses auction results, market trends, and comparable sales weekly.</p></div>
                    </div>
                  </div>
                  <Button onClick={() => setDemoStep(5)} className="bg-[#627EEA] text-white text-xs w-full">What Can I Do Next?</Button>
                </div>
              )}

              {demoStep === 5 && (
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
                  <Zap size={20} className="text-gold mb-2" />
                  <p className="text-white text-sm mb-3">What You Can Do With Your Tokens</p>
                  <div className="space-y-3 mb-4">
                    <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-xs text-white mb-1">Hold & Appreciate</p><p className="text-[10px] text-muted-foreground">View your asset's value on its detail page. The Oracle AI updates valuations weekly based on real auction data and market conditions. No action needed.</p></div>
                    <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-xs text-white mb-1">Sell Fractions on the Marketplace</p><p className="text-[10px] text-muted-foreground">Go to Marketplace &gt; My Holdings &gt; click "List for Sale". Set your price per fraction and quantity. Buyers purchase directly, funds settle to your wallet instantly.</p></div>
                    <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-xs text-white mb-1">Use as Collateral (Coming Soon)</p><p className="text-[10px] text-muted-foreground">Lock your tokens in a smart contract to borrow GBP against them. Like a mortgage on your digital asset, without selling it.</p></div>
                    <div className="p-3 bg-[#0F0F0F] rounded"><p className="text-xs text-white mb-1">Transfer Ownership</p><p className="text-[10px] text-muted-foreground">Send fractions to any Ethereum wallet address (a private buyer, family member, or trust). Open MetaMask &gt; select your token &gt; click "Send" &gt; enter recipient address. The transfer is instant and permanent. You can also do this through the Assagart marketplace for added safety.</p></div>
                  </div>
                  <Button onClick={() => { setShowDemo(false); setDemoStep(0); }} variant="outline" className="border-gold/30 text-gold text-xs w-full">Finish Demo</Button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Blockchain Essentials + Gas Fees */}
        <section className="grid md:grid-cols-2 gap-4 mb-10" data-testid="blockchain-basics">
          {[
            { q: 'What is a Digital Twin?', a: 'A blockchain-secured digital copy of your physical asset. It proves ownership, tracks provenance, and enables fractional ownership.' },
            { q: 'What is MetaMask?', a: 'A crypto wallet that lives in your browser. It stores your Ethereum address and lets you sign transactions to buy/sell fractions. You can also view your tokens independently of Assagart.' },
            { q: 'Are my assets safe?', a: 'Your physical asset stays in insured custody. The blockchain record is a certificate of ownership, not the asset itself. You always retain physical possession.' },
            { q: 'What about gas fees?', a: 'Gas fees are small Ethereum network transaction costs (typically £3-£15). Assagart covers all gas fees from our treasury wallet, so you never pay network costs directly.' },
          ].map((item, i) => (
            <div key={i} className="luxury-card p-4">
              <h4 className="text-white text-sm font-medium mb-1">{item.q}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.a}</p>
            </div>
          ))}
        </section>

        {/* Bottom CTA */}
        <section className="luxury-card p-8 text-center" data-testid="tokenisation-cta">
          {hasDTAccess ? (
            <>
              <KeyRound size={28} className="text-gold mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-white mb-3">Ready to Tokenise?</h2>
              <p className="text-muted-foreground text-sm mb-6 max-w-lg mx-auto">
                Visit any of your assets and click "Activate Digital Twin" to begin.
              </p>
              <Link to="/assets">
                <Button className="btn-primary flex items-center gap-2 mx-auto" data-testid="go-to-assets-btn">
                  <KeyRound size={14} /> Go to My Assets <ArrowRight size={14} />
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Globe size={28} className="text-gold mx-auto mb-4" />
              <h2 className="font-serif text-2xl text-white mb-3">Start Tokenising Your Assets</h2>
              <p className="text-muted-foreground text-sm mb-3 max-w-lg mx-auto">
                Join the growing number of collectors securing their luxury portfolios on the blockchain.
              </p>
              <p className="text-gold text-xs mb-6">One-time access fee of £499</p>
              <Link to="/assets">
                <Button className="btn-primary flex items-center gap-2 mx-auto" data-testid="request-dt-btn">
                  <Lock size={14} /> Request Digital Twin Access <ArrowRight size={14} />
                </Button>
              </Link>
            </>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
